import AppBackButton from '@/components/AppBackButton';
import { Logo } from '@/components/Logo';
import { GridBackdrop } from '@/components/UI/GridBackdrop';
import Footer from '@/renderer/Footer';
import { useIsClient } from '@/renderer/utils/hooks/useIsClient';
import { isNativePlatform } from '@/renderer/utils/isNativePlatform';
import { SafeTopOwner } from '@/renderer/utils/safeTop';

/**
 * Shared chrome for the standalone auth screens (sign in, reset password): a
 * centered branded card on a soft forest-green backdrop, with the app footer
 * below. Keeps the auth pages visually in lockstep so they can't drift apart.
 *
 * In the native app the marketing footer is dropped (it makes the screen long
 * and un-app-like) and a Back button takes its place: the app has no browser
 * chrome, so without one these screens are a dead end (#2039). Both live here
 * rather than in the individual pages so sign-in and password reset can't drift
 * apart. The register page is not an AuthShell screen and renders its own.
 */
export function AuthShell({
  children,
  hideLogo = false,
}: {
  children: React.ReactNode;
  hideLogo?: boolean;
}) {
  // Client-only: the prerendered markup is shared by web and the app bundle, so
  // the footer must render on the server and only drop out once we know we're
  // in the native shell.
  const isClient = useIsClient();
  const isNative = isClient && isNativePlatform();

  return (
    <div className="relative flex min-h-full flex-col bg-[Canvas]">
      {/* Branded backdrop: soft forest-green glow + very faint grid, masked to
          fade out. Shared with the native landing (#2039). */}
      <GridBackdrop />

      {/* Native-only, and fixed to the viewport, so it stays put when the
          keyboard scrolls this centered block under it. Renders nothing on web. */}
      <AppBackButton />

      {/* py includes the iOS safe-area insets: the native shell paints
          edge-to-edge, and with the keyboard up this centered block shrinks
          until the logo would sit under the notch / Dynamic Island (env() is 0
          on web). SafeTopOwner: this block owns the top inset, so the layout
          adds none of its own (#2028). */}
      <div className="relative flex flex-1 items-center justify-center px-6 pt-[calc(3rem+var(--safe-top))] pb-[calc(3rem+var(--safe-bottom))] sm:px-6">
        <SafeTopOwner />
        {/* On narrow/native screens the form goes edge-to-edge with no card
            framing — a bordered card floating inside an already-full-screen
            mobile viewport reads as redundant. From sm up it becomes the
            centered branded card (border + shadow + max-width). */}
        <div className="w-full max-w-[390px] rounded-none border-0 bg-transparent p-0 shadow-none sm:rounded-2xl sm:border sm:border-zinc-950/10 sm:bg-white sm:p-8 sm:shadow-lg dark:bg-transparent dark:shadow-none sm:dark:border-white/10 sm:dark:bg-zinc-900/70 sm:dark:shadow-black/40 sm:dark:backdrop-blur-sm">
          {!hideLogo && (
            <div className="mb-5 flex justify-center">
              <a
                href="/"
                aria-label="RAS home"
                className="block size-[58px] select-none transition hover:opacity-90"
              >
                <Logo className="size-full" width={58} />
              </a>
            </div>
          )}
          {children}
        </div>
      </div>
      {!isNative && <Footer />}
    </div>
  );
}

/** Full-width forest-green primary button styling shared across auth forms. */
export const authPrimaryButtonClass =
  'w-full bg-[#15734A] font-bold hover:bg-[#125f3d] focus-visible:outline-[#15734A] dark:bg-[#34B473] dark:text-zinc-950 dark:hover:bg-[#43c983]';

/** Forest-green inline link styling shared across auth forms. */
export const authLinkClass = 'font-semibold text-[#15734A] dark:text-[#34B473]';
